import { useAuth0 } from '@auth0/auth0-react';
import { auth0ClaimKey } from '../config/deployment';
import type { AuthStatus, Auth0AppMetadata, PlatformRole } from '../types/auth0';

/**
 * The platform role a session carries. Two sources, same precedence as the
 * gateway's `resolvePlatformRoleFromClaims`:
 *
 * 1. `app_metadata.platformRole` — the AUTHORITATIVE value. It is what the
 *    gateway writes on invitation acceptance and admin promotion, and the
 *    Post-Login Action copies the whole app_metadata onto the ID token. Only
 *    the literal `admin` / `operator` count; anything else is no role.
 * 2. Legacy Auth0 RBAC roles (`admin` / `operator` in the `/roles` claim),
 *    assigned by hand in the Auth0 dashboard — still honored so existing staff
 *    accounts keep working.
 *
 * Before source 1 was read here, an invited operator re-authenticated into a
 * token that carried the role in app_metadata and was still routed to the
 * customer app, because only source 2 was consulted.
 */
export function derivePlatformRole(
  roles: readonly string[],
  appMetadata: Pick<Auth0AppMetadata, 'platformRole'>,
): PlatformRole | null {
  const fromMetadata = appMetadata.platformRole;
  if (fromMetadata === 'admin' || fromMetadata === 'operator') return fromMetadata;
  if (roles.includes('admin')) return 'admin';
  if (roles.includes('operator')) return 'operator';
  return null;
}

export function useAuthStatus(): AuthStatus {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout: auth0Logout,
  } = useAuth0();

  // Roles from Auth0 (set via Post-Login Action)
  const roles = (user?.[auth0ClaimKey('roles')] as string[]) || [];

  // App metadata for the platform role, approval status and onboarding
  const appMetadata = (user?.[auth0ClaimKey('app_metadata')] as Auth0AppMetadata) || {};

  const platformRole = derivePlatformRole(roles, appMetadata);
  const isAdmin = platformRole === 'admin';
  const isOperator = platformRole === 'operator';
  const isPlatformUser = isAdmin || isOperator;
  const isRegularUser = !isPlatformUser;

  // Organization (Firm) info from Auth0 Organizations
  const firmId = user?.[auth0ClaimKey('org_id')] as string | undefined;
  const firmName = user?.[auth0ClaimKey('org_name')] as string | undefined;
  const isApproved = appMetadata.approved === true || user?.[auth0ClaimKey('approved')] === true;

  // Onboarding check: skip for admins and already-approved users (legacy users created before onboarding feature)
  const onboardingCompleted =
    isAdmin ||
    isApproved ||
    appMetadata.onboardingCompleted === true ||
    !!appMetadata.usageType;
  const canAccessApp = isAuthenticated && isApproved;

  const login = () => loginWithRedirect();

  const signup = () => loginWithRedirect({
    authorizationParams: {
      screen_hint: 'signup',
    },
  });

  const logout = () => auth0Logout({
    logoutParams: {
      returnTo: window.location.origin,
    },
  });

  return {
    // Identity
    userId: user?.sub,
    email: user?.email,
    name: user?.name,

    // Organization (Firm)
    firmId,
    firmName,

    // Roles
    roles,
    isAdmin,
    isOperator,
    isPlatformUser,
    platformRole,
    isRegularUser,

    // Approval & Onboarding
    isApproved,
    onboardingCompleted,
    canAccessApp,

    // Auth state
    isAuthenticated,
    isLoading,
    user,

    // Actions
    login,
    signup,
    logout,
  };
}
